import classNames from 'classnames'
import {t} from 'i18next'
import {Dropdown} from 'primereact/dropdown'
import {Fieldset} from 'primereact/fieldset'
import {InputMask} from 'primereact/inputmask'
import {InputNumber} from 'primereact/inputnumber'
import {InputText} from 'primereact/inputtext'
import {InputTextarea} from 'primereact/inputtextarea'
import React from 'react'
import {Controller, useFormContext} from 'react-hook-form'
import i18n from '../../../../i18n'
import { JournalRythmCt } from '../../../../model/enums/enums'
import {getFormErrorMessage} from '../../../util/ErrorText'
import {ConfessionDropdown} from '../../../util/form/dropdown/confession-dropdown/ConfessionDropdown'
import {SizeDropdown} from '../../../util/form/dropdown/size-dropdown/SizeDropdown'
import {dominantHand} from '../../account/profile/constants/dominantHand.const'
import {driverLicense} from '../../account/profile/constants/driverLicense.const'
import {nativeLanguage} from '../../account/profile/constants/nativeLanguage.const'
import { travelCard } from '../../account/profile/constants/publicTransport.const'

// to be used in form context
// describes the extended data of a person
export const ExtendedPersonData = (): JSX.Element => {

	const {control, formState: {errors}} = useFormContext()

	return <Fieldset
		legend={t<string>('hrm.advanced')}
		toggleable
		className={'min-width-220'}>
		<div className="p-fluid formgrid grid">
			<div className="field col-12 md:col-3 mb-2">
				<label
					htmlFor="health_insurance_number"
					className={classNames({'p-error': errors.health_insurance_number})}>
					{t<string>('person.health_insurance_number')}
				</label>
				<Controller name="health_insurance_number"
				            rules={{pattern: /^[0-9]{3}.[0-9]{4}.[0-9]{4}.[0-9]{2}/}}
				            control={control}
				            render={({field, fieldState}) => (
					            <InputMask
						            id={field.name}
						            {...field}
						            value={field.value || ''}
						            autoClear={false}
						            mask="999.9999.9999.99"
						            placeholder={'756.1234.5678.97'}
						            onChange={e => field.onChange(e)}
						            className={classNames({'p-invalid': fieldState.error})}
					            />
				            )}/>
			</div>
			<div className="field col-12 md:col-3 mb-2">
				<label
					htmlFor="bank_name"
					className={classNames({'p-error': errors.bank_name})}>{t<string>('person.bank_name')}
				</label>
				<Controller name="bank_name" control={control} render={({field, fieldState}) => (
					<InputText
						id={field.name}
						{...field}
						maxLength={50}
						value={field.value || ''}
						className={classNames({'p-invalid': fieldState.error})}/>
				)}/>
				{getFormErrorMessage(errors, 'bank_name')}
			</div>

			<div className="field col-12 md:col-6 mb-2">
				<label
					htmlFor="iban"
					className={classNames({'p-error': errors.iban})}>{t<string>('person.iban')}</label>

				<Controller name="iban" control={control} render={({field}) => (
					<InputMask
						id={field.name}
						{...field}
						value={field.value || ''}
						maxLength={42}
						unmask={true}
						slotChar={'_'}
						autoClear={false}
						mask="aa** **** **** **** **** **** **** **** **"
						placeholder={'CH12 3456 7898 7654 3212 R'}
					/>
				)}/>

				{getFormErrorMessage(errors, 'iban')}
			</div>

			<div className="field col-12 md:col-3 mb-2">
				<label htmlFor="workload_in_percent">{t<string>('person.workload_in_percent')}</label>
				<Controller name="workload_in_percent" control={control}
				            render={({field}) => (
					            <InputNumber
						            inputId="percent"
						            min={0}
						            max={100}
						            id={field.name}
						            value={field.value || ''}
						            onValueChange={(e) => {
							            field.onChange(e.value)
						            }
						            }
						            suffix="%"/>
				            )}/>
			</div>

			<div className="field col-12 md:col-3 mb-2">
				<label
					htmlFor="confession"
					className={classNames({'p-error': errors.confession})}>{t<string>('person.confession')}</label>
				<ConfessionDropdown/>
			</div>

			<div className="field col-12 md:col-3 mb-2">
				<label htmlFor="cloth_size">{t<string>('person.cloth_size')}</label>
				<SizeDropdown/>
			</div>
			<div className="field lg:col-3 col-12">
				<label htmlFor="dominant_hand">
					{i18n.t<string>('person.dominant_hand')}
				</label>
				<Controller
					name="dominant_hand"
					control={control}
					render={({field, fieldState}) => <Dropdown
						options={dominantHand}
						placeholder={i18n.t<string>('dominant_hand.help.choose_dominant_hand')}
						className={classNames({'p-invalid': fieldState.error})}
						{...field} />}/>
			</div>
			<div className="field lg:col-3 col-12">
				<label htmlFor="vehicle_license">
					{i18n.t<string>('person.vehicle_license')}
				</label>
				<Controller
					name="vehicle_license"
					control={control}
					render={({field, fieldState}) => <Dropdown
						options={driverLicense}
						placeholder={i18n.t<string>('core.choose')}
						className={classNames({'p-invalid': fieldState.error})}
						{...field} />}/>
			</div>
			<div className="field lg:col-3 col-12">
				<label htmlFor="travel_card">
					{i18n.t<string>('person.public_transport.title')}
				</label>
				<Controller
					name="travel_card"
					control={control}
					render={({field, fieldState}) => <Dropdown
						options={travelCard}
						editable
						maxLength={40}
						placeholder={i18n.t<string>('core.choose')}
						className={classNames({'p-invalid': fieldState.error})}
						{...field} />}/>
			</div>
			<div className="field lg:col-3 col-12">
				<label htmlFor="native_language">
					{i18n.t<string>('person.language')}
				</label>
				<Controller
					name="id_first_language"
					control={control}
					render={({field, fieldState}) => <Dropdown
						options={nativeLanguage}
						placeholder={i18n.t<string>('language.help.choose_language')}
						className={classNames({'p-invalid': fieldState.error})}
						{...field} />}/>
			</div>
			<div className="field col-12 md:col-3 mb-2">

				<label
					htmlFor="slack_user_id"
					className={classNames({'p-error': errors.slack_user_id})}>
					{t<string>('person.slack_user_id')}
				</label>
				<Controller name="slack_user_id" control={control} render={({field}) => (
					<InputText
						id={field.name}
						{...field}
						placeholder={'U03NAK32S11'}
						maxLength={21}
						value={field.value || ''}
					/>
				)}/>
				{getFormErrorMessage(errors, 'slack_user_id')}
			</div>
			<div className="field col-12 md:col-12 mb-2">
				<label
					htmlFor="comment"
					className={classNames({'p-error': errors.comment})}>{t<string>('person.comment')}</label>
				<Controller name="comment" control={control} render={({field}) => (
					<InputTextarea
						id={field.name}
						{...field}
						value={field.value || ''}
						rows={5} autoResize/>
				)}/>
			</div>
		</div>
	</Fieldset>
}